import { isWaiting, stageMismatch, STAGE_MISMATCH_LABELS } from "../lib/schedule";
import { headlineValue, money, splitAddress, type JobCardFaceProps } from "./cardFaceTypes";
import styles from "./CardFaceAAddressLed.module.css";

export const VARIANT_A_NAME = "Address-led + priority rail";

/**
 * PROTOTYPE ONLY.
 *
 * Premise: a job is a place. "Service split-system air con…" describes fifty
 * jobs; "211 Victoria Square, Adelaide" describes one. So the address is the
 * headline and the scope text drops to a subtitle — where truncating it costs
 * nothing, because it is a category, not an identity.
 *
 * Priority moves off the face entirely and onto a colour rail, which is
 * readable at a glance across a whole column without spending a line.
 * References drop to the footer at 10px: needed when you are on the phone to a
 * client, never needed while scanning.
 */
export function VariantAAddressLed({ card }: JobCardFaceProps) {
  const { street, locality } = splitAddress(card.propertyAddress);
  const value = headlineValue(card);
  const waiting = isWaiting(card.scheduleAction ?? "");
  const mismatch = stageMismatch(card.scheduleAction ?? "", card.columnId ?? "");
  const schedule = [card.scheduleLabel, card.scheduleWhen].filter(Boolean).join(" · ");
  const refs = [card.refInternal && `#${card.refInternal}`, card.refExternal && `Ext ${card.refExternal}`]
    .filter(Boolean)
    .join("  ");

  return (
    <div className={styles.face} data-priority={card.priority ?? "normal"}>
      <span className={styles.rail} aria-hidden />
      <div className={styles.body}>
        <strong className={styles.street}>{street || "No address"}</strong>
        {locality && <span className={styles.locality}>{locality}</span>}
        <span className={styles.scope} title={card.title}>
          {card.title}
        </span>

        {schedule && (
          <span className={waiting ? styles.scheduleWaiting : styles.schedule}>
            {waiting ? "⏳" : "→"} {schedule}
            {mismatch && (
              <span className={styles.flag} title={STAGE_MISMATCH_LABELS[mismatch]}>
                !
              </span>
            )}
          </span>
        )}

        <div className={styles.footer}>
          <span className={styles.client}>{card.clientName ?? "—"}</span>
          {value && (
            <span className={value.estimated ? styles.valueEstimated : styles.value}>
              {money(value.cents)}
              {value.estimated && <em className={styles.est}>est</em>}
            </span>
          )}
        </div>
        {refs && <span className={styles.refs}>{refs}</span>}
      </div>
    </div>
  );
}
